import {
  isForcedAlignedTranscript,
  prepareTranscriptTokens,
} from '@src/editor/services/transcriptValidations';
import { requestMissingFields } from '@src/utility/story';
import { extractTranscriptionText } from '@src/utility/transcriptFormatUtils';
import { makeAutoObservable, observable } from 'mobx';

import { TranscriptVersion } from '../repositories/TranscriptRepository';
import { TranscriptionStatus } from '../types.ts/video';

import type { TranscriptRepository } from '../repositories/TranscriptRepository';
import type {
  TranscriptChange,
  TranscriptData,
  TranscriptElement,
  TranscriptElementWithClipSegmentAttributes,
  TranscriptTextElement,
} from '../types.ts/transcript';
import type { RootStore } from './RootStore';

/**
 * Interface for transcript version URLs
 */
export interface TranscriptVersionUrls {
  [TranscriptVersion.LATEST]: string;
  [TranscriptVersion.ORIGINAL]?: string;
  [TranscriptVersion.REV]?: string;
  [TranscriptVersion.AWS]?: string;
  [TranscriptVersion.ASSEMBLY]?: string;
  [TranscriptVersion.AWS_REV]?: string;
}

export class TranscriptionStore {
  // Core state
  transcriptionElements: TranscriptElementWithClipSegmentAttributes[] = [];
  transcriptionChanges: TranscriptChange[] = [];
  originalTranscription: TranscriptData | null;
  transcriptionLoadingStatus: TranscriptionStatus = TranscriptionStatus.Idle;
  transcriptionUrl: string = '';
  transcriptionVersionUrls: TranscriptVersionUrls = {
    [TranscriptVersion.LATEST]: '',
  };
  currentTranscriptVersion: TranscriptVersion = TranscriptVersion.LATEST;
  hasUnsavedChanges: boolean = false;
  language: string = 'en';
  /**
   * The story whose transcript `loadTranscription` last loaded. The store is
   * shared across stories, so a consumer that must not read the previous
   * story's transcript during a switch compares this to its own story id.
   */
  loadedStoryId: string | null = null;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    // Set to null by default
    this.originalTranscription = null;
    makeAutoObservable(this, {
      transcriptionElements: observable.shallow,
      originalTranscription: observable.ref,
    });
  }

  /**
   * PLAT-10788 — the transcript's RAW language, or `null` when it has none.
   * Unlike `language` (coalesced to `'en'` on load —
   * `setLanguage(transcriptData.language || 'en')`), this preserves the
   * "no language" signal so per-language karaoke keying can fall through to the
   * story's `originalVideoLanguage` (see `resolveSourceLanguageKey`). Use this —
   * not `language` — anywhere a source karaoke variant is keyed; otherwise the
   * coalesced `'en'` masks the `originalVideoLanguage` fallback and mis-keys the
   * source variant (`en` instead of e.g. `en-US`).
   */
  get rawLanguage(): string | null {
    return this.originalTranscription?.language || null;
  }

  get isForcedAligned(): boolean {
    return isForcedAlignedTranscript(this.originalTranscription);
  }

  // Repository accessor. This is a private method that should only be used internally
  // but due to intialization order, while it should always be available, it may not be
  // so we need to handle that case.
  private get repository(): TranscriptRepository {
    if (!this.rootStore.datoClientStore.transcriptRepository) {
      throw new Error('Transcript repository not initialized');
    }
    return this.rootStore.datoClientStore.transcriptRepository;
  }

  /**
   * Loads transcription data for a video
   * @param storyId The story ID to load transcription for
   * @param useAws Whether to use AWS hosted transcription
   * @param signal Optional AbortSignal for request cancellation
   */
  async loadTranscription(
    storyId: string,
    useAws: boolean,
    signal?: AbortSignal,
  ) {
    this.setTranscriptionLoadingStatus(TranscriptionStatus.Loading);

    try {
      let transcriptionInfo;
      if (useAws) {
        const { transcription: selfHostedTranscription } =
          await requestMissingFields(storyId, ['transcription'], signal);
        transcriptionInfo = selfHostedTranscription;
      } else {
        transcriptionInfo =
          await this.repository.getTranscriptionFromDato(storyId);
      }
      console.log('transcriptionInfo:', transcriptionInfo, useAws);
      if (
        !transcriptionInfo ||
        !transcriptionInfo.elementsJson ||
        !transcriptionInfo.elementsJson.url
      ) {
        this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
        throw new Error('No transcription data available');
      }

      // Store all version URLs - these should be provided by the API
      // For now, we're just storing the latest version URL
      const transcriptUrl = transcriptionInfo.elementsJson.url;
      this.setTranscriptionUrl(transcriptUrl);
      this.setTranscriptionVersionUrls({
        [TranscriptVersion.LATEST]: transcriptUrl,
        // The backend should provide these URLs:
        [TranscriptVersion.ORIGINAL]: (transcriptionInfo.elementsJson as any)
          .originalUrl,
        [TranscriptVersion.REV]: (transcriptionInfo.elementsJson as any).revUrl,
        [TranscriptVersion.AWS]: (transcriptionInfo.elementsJson as any).awsUrl,
        [TranscriptVersion.ASSEMBLY]: (transcriptionInfo.elementsJson as any)
          .assemblyUrl,
        [TranscriptVersion.AWS_REV]: (transcriptionInfo.elementsJson as any)
          .awsRevUrl,
      });

      // Load the current version (default is LATEST)
      await this.loadTranscriptVersion(TranscriptVersion.LATEST);

      this.setLoadedStoryId(storyId);
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loaded);
    } catch (error) {
      console.error('Error loading transcription:', error);
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
    }
  }

  /**
   * Loads a specific version of the transcript
   * @param version The transcript version to load
   * @returns A promise that resolves when the transcript is loaded
   */
  async loadTranscriptVersion(version: TranscriptVersion): Promise<boolean> {
    try {
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loading);

      // Ensure we have a URL for this version
      const versionUrl = this.transcriptionVersionUrls[version];
      if (!versionUrl) {
        console.error(`No URL available for transcript version: ${version}`);
        this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
        return false;
      }
      console.log('versionUrl:', versionUrl);
      // Fetch and load the transcript data
      const transcriptData =
        await this.repository.fetchTranscriptFromUrl(versionUrl);
      console.log('transcriptData:', transcriptData);
      transcriptData.elements = prepareTranscriptTokens(
        transcriptData.elements,
        isForcedAlignedTranscript(transcriptData),
      ) as TranscriptElement[];
      this.setOriginalTranscription(transcriptData);
      this.setTranscriptionElements(transcriptData.elements);
      this.setLanguage(transcriptData.language || 'en');
      this.setCurrentTranscriptVersion(version);

      // Reset changes when switching to a different version
      this.setTranscriptionChanges([]);
      this.setHasUnsavedChanges(false);

      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loaded);
      return true;
    } catch (error) {
      console.error(`Error loading transcript version ${version}:`, error);
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
      return false;
    }
  }

  /**
   * Get final transcription elements with all changes applied
   */
  getFinalTranscriptionElements() {
    return this.transcriptionElements;
  }

  /**
   * Get original transcription data before any changes
   */
  getOriginalTranscription() {
    return this.originalTranscription;
  }

  /**
   * Get the full text of the transcription
   */
  getTranscriptionText(elements?: TranscriptElement[]) {
    return extractTranscriptionText(elements || this.transcriptionElements);
  }

  /**
   * Check if transcription is available and ready to use
   */
  get isTranscriptionReady() {
    return (
      this.transcriptionLoadingStatus === TranscriptionStatus.Loaded &&
      this.originalTranscription !== null &&
      this.transcriptionElements.length > 0
    );
  }

  get isStoryOriginalTranscriptionAvailable() {
    return this.originalTranscription !== null;
  }

  /**
   * Check if transcription is currently loading
   */
  get isTranscriptionLoading() {
    return this.transcriptionLoadingStatus === TranscriptionStatus.Loading;
  }

  /**
   * Get all transcription changes
   */
  getTranscriptionChangesForSave() {
    // remove all add_karaoke_break changes if change.command is addKaraokeBreak(all) except the latest one of the kind
    // to reduce size of array to save 'cause later one overrides the previous ones
    const addKaraokeBreakLastChangeIndex =
      // @ts-ignore
      this.transcriptionChanges.findLastIndex(
        (change: TranscriptChange) => change.command === 'addKaraokeBreak(all)',
      );
    // remove all but the last one from the array
    if (addKaraokeBreakLastChangeIndex !== -1) {
      return this.transcriptionChanges.filter(
        (change: TranscriptChange, index: number) =>
          change.command !== 'addKaraokeBreak(all)' ||
          index === addKaraokeBreakLastChangeIndex,
      );
    } else {
      return [...this.transcriptionChanges];
    }
  }

  getFinalTranscriptionSlice(startIdx: number, endIdx: number) {
    return this.transcriptionElements.slice(startIdx, endIdx);
  }

  /**
   * Reset the transcription store state
   */
  reset() {
    this.transcriptionElements = [];
    this.transcriptionChanges = [];
    this.originalTranscription = this.defaultTranscriptData();
    this.transcriptionLoadingStatus = TranscriptionStatus.Idle;
    this.transcriptionUrl = '';
    this.transcriptionVersionUrls = {
      [TranscriptVersion.LATEST]: '',
    };
    this.currentTranscriptVersion = TranscriptVersion.LATEST;
    this.hasUnsavedChanges = false;
    this.language = 'en';
    this.loadedStoryId = null;
  }

  // Private setters
  private setTranscriptionElements(
    elements: TranscriptElementWithClipSegmentAttributes[],
  ) {
    this.transcriptionElements = elements;
  }

  public setOriginalTranscription(transcriptData: TranscriptData) {
    const processedElements = prepareTranscriptTokens(
      transcriptData.elements,
      isForcedAlignedTranscript(transcriptData),
    ) as TranscriptElement[];

    this.originalTranscription = {
      ...transcriptData,
      elements: processedElements,
    };
  }

  private setTranscriptionLoadingStatus(status: TranscriptionStatus) {
    this.transcriptionLoadingStatus = status;
  }

  private setLoadedStoryId(storyId: string) {
    this.loadedStoryId = storyId;
  }

  private setTranscriptionUrl(url: string) {
    this.transcriptionUrl = url;
  }

  setTranscriptionVersionUrls(urls: Partial<TranscriptVersionUrls>) {
    this.transcriptionVersionUrls = {
      ...this.transcriptionVersionUrls,
      ...urls,
    };
  }

  private setCurrentTranscriptVersion(version: TranscriptVersion) {
    this.currentTranscriptVersion = version;
  }

  private setHasUnsavedChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  private setLanguage(language: string) {
    this.language = language;
  }

  private setTranscriptionChanges(changes: TranscriptChange[]) {
    this.transcriptionChanges = changes;
  }

  /**
   * Update transcription elements from the VideoTranscriptionProcessor
   * This method enables two-way sync during the transition period
   * @param elements Updated transcription elements
   */
  updateFromProcessor(elements: TranscriptElementWithClipSegmentAttributes[]) {
    this.setTranscriptionElements(elements);
    this.setHasUnsavedChanges(true);
  }

  /**
   * Set transcription changes from the VideoTranscriptionProcessor
   * @param changes Updated transcription changes
   */
  setChangesFromProcessor(changes: TranscriptChange[]) {
    this.setTranscriptionChanges(changes);
    this.setHasUnsavedChanges(true);
  }

  /**
   * Get available transcript versions
   * @returns Array of available transcript versions
   */
  getAvailableVersions(): TranscriptVersion[] {
    return Object.entries(this.transcriptionVersionUrls)
      .filter(([, url]) => !!url)
      .map(([version]) => version as TranscriptVersion);
  }

  /**
   * Get the current transcript version
   * @returns The current transcript version
   */
  getCurrentVersion(): TranscriptVersion {
    return this.currentTranscriptVersion;
  }

  async regenerateTranscription() {
    // TODO FIX, re-work
    throw new Error('Not implemented');
    // try {
    //   await this.saveCurrentStory();
    //   await this.datoClientStore.storyRepository!.deleteTranscription(
    //     this.story!.id,
    //   );
    //   this.story!.transcription = undefined;
    //   this.finalTranscriptionElements = undefined;
    //   this.originalVideoAudioUrl = '';
    //   this.originalWaveForm = null;
    //   this.transcriptionLoadingStatus = TranscriptionStatus.Loaded;
    //   this.pollTranscriptionUrl();
    // } catch (err) {
    //   this.transcriptionLoadingStatus = TranscriptionStatus.Failed;
    // }
  }

  private defaultTranscriptData(): TranscriptData | null {
    return null;
  }

  /**
   * Check if the transcript is compatible with the transcript editor
   * This is used in conjunction with the LaunchDarkly flag 'enableTranscriptEditor'
   * to determine if the transcript editor tab should be shown.
   *
   * Older transcripts won't have paragraph_id on the text elements and are not
   * compatible with the transcript editor.
   *
   * @returns {boolean} True if transcript is compatible with the editor
   */
  get isTranscriptEditorCompatible(): boolean {
    // If no transcription is loaded, it's not compatible
    if (!this.originalTranscription || !this.transcriptionElements.length) {
      return false;
    }

    // Get the first text element (not punct)
    const firstTextElement = this.transcriptionElements.find(
      (element): element is TranscriptTextElement => element.type === 'text',
    );

    // If we can't find a text element, it's not compatible
    if (!firstTextElement) {
      return false;
    }

    // Check if it has paragraph_id - newer transcripts will have this
    return !!firstTextElement.paragraph_id;
  }

  getTextFromRange(startIndex: number, endIndex: number): string {
    const elements = this.getFinalTranscriptionElements();
    if (!elements.length) return '';
    if (startIndex > endIndex) return '';
    if (
      startIndex < 0 ||
      startIndex >= elements.length ||
      endIndex >= elements.length
    ) {
      return '';
    }
    let result = '';
    for (let i = startIndex; i <= endIndex; i++) {
      const el = elements[i];
      if (!el) continue;
      if (el.state === 'removed' || el.state === 'cut') continue;
      result += el.value || '';
    }
    return result;
  }

  /**
   * Replaces the current transcript with provided data without fetching from network
   * Used after saving to update store with the fresh saved data, avoiding cache issues
   * @param transcriptData The transcript data to replace with
   * @param version The version this transcript represents (defaults to LATEST)
   */
  replaceTranscriptVersion(
    transcriptData: TranscriptData,
    version: TranscriptVersion = TranscriptVersion.LATEST,
  ): void {
    const processedElements = prepareTranscriptTokens(
      transcriptData.elements,
      isForcedAlignedTranscript(transcriptData),
    ) as TranscriptElement[];

    this.setOriginalTranscription({
      ...transcriptData,
      elements: processedElements,
    });
    this.setTranscriptionElements(processedElements);
    this.setLanguage(transcriptData.language || 'en');
    this.setCurrentTranscriptVersion(version);
  }
}
